import React, { useState, useEffect } from "react";
import "../../index.css";
import "../../App.css";
import { Row, Col, Input, Typography, Radio, Button, Spin } from "antd";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import {
  handleGoToNextStep,
  handleUpdateErrorsOfOrderCreamForm,
  handleUpdatePainDetails,
  handleGoBack,
  saveStep4Api,
  getOrderByTransactionIdApi,
} from "../../redux-toolkit/slices/orderCreamSlice";
import ErrorList from "../Items/ErrorsList";
import { validateOrderCreamPainDetails } from "../../schemas/OrderCream";
import { useSearchParams } from "react-router-dom";

const OcConditionspecificq = () => {
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);
  const {
    painDetails,
    isError,
    errors,
    currentStep,
    transactionId,
    isLoading,
  } = orderCreamForm;
  const {
    impactedJoints,
    swelling,
    sleepInterference,
    burningSensation,
    specificStimuli,
  } = painDetails;
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  // Fetch order data when component mounts
  useEffect(() => {
    const fetchOrder = async () => {
      if (transactionId) {
        setIsLoadingOrder(true);
        try {
          const orderData = await dispatch(
            getOrderByTransactionIdApi(transactionId)
          ).unwrap();
          // The Redux reducer will automatically populate the state with order data
          // including painDetails if it exists
        } catch (error) {
          console.error("Error loading order:", error);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    fetchOrder();
  }, [transactionId, dispatch]);

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", prevStep.toString());
      setSearchParams(newParams, { replace: true });
      // Don't call handleGoBack() - let the parent component sync from URL
    }
  };
  const handleUpdateValue = (
    key: keyof OrderCreamPainDetails,
    value: string
  ) => {
    dispatch(handleUpdatePainDetails({ key: key, value: value }));
  };
  const handleSubmit = async () => {
    // Validate radio buttons first - must be "yes" or "no"
    const validValues = ["yes", "no"];

    if (!impactedJoints || 
        impactedJoints === "" || 
        impactedJoints === null || 
        impactedJoints === undefined ||
        (typeof impactedJoints === 'string' && impactedJoints.trim() === "") ||
        !validValues.includes(impactedJoints)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose impacted joints."],
        })
      );
    }

    if (!swelling || 
        swelling === "" || 
        swelling === null || 
        swelling === undefined ||
        (typeof swelling === 'string' && swelling.trim() === "") ||
        !validValues.includes(swelling)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose swelling."],
        })
      );
    }

    if (!sleepInterference || 
        sleepInterference === "" || 
        sleepInterference === null || 
        sleepInterference === undefined ||
        (typeof sleepInterference === 'string' && sleepInterference.trim() === "") ||
        !validValues.includes(sleepInterference)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose sleep interference."],
        })
      );
    }

    if (!burningSensation || 
        burningSensation === "" || 
        burningSensation === null || 
        burningSensation === undefined ||
        (typeof burningSensation === 'string' && burningSensation.trim() === "") ||
        !validValues.includes(burningSensation)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose burning sensation."],
        })
      );
    }

    if (!specificStimuli || 
        specificStimuli === "" || 
        specificStimuli === null || 
        specificStimuli === undefined ||
        (typeof specificStimuli === 'string' && specificStimuli.trim() === "") ||
        !validValues.includes(specificStimuli)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose specific stimuli."],
        })
      );
    }

    // Remove _id before validation
    const { _id, ...painDetailsWithoutId } = painDetails as any;
    const { isError, errors } =
      validateOrderCreamPainDetails(painDetailsWithoutId);

    if (isError) {
      return dispatch(handleUpdateErrorsOfOrderCreamForm({ isError, errors }));
    }

    if (!transactionId) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Transaction ID is missing. Please refresh the page."],
        })
      );
    }

    try {
      // Sanitize painDetails to remove _id fields
      const sanitizedPainDetails = {
        impactedJoints: painDetails.impactedJoints,
        swelling: painDetails.swelling,
        sleepInterference: painDetails.sleepInterference,
        burningSensation: painDetails.burningSensation,
        specificStimuli: painDetails.specificStimuli,
      };

      await dispatch(
        saveStep4Api({
          transactionId,
          painDetails: sanitizedPainDetails,
        })
      ).unwrap();

      // Update URL with new step
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", "5");
      setSearchParams(newParams, { replace: true });

      dispatch(handleGoToNextStep());
    } catch (error) {
      dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Failed to save step. Please try again."],
        })
      );
    }
  };
  return (
    <div className="condition-specific-questions-entry">
      <div className="flex flex-col gap-5 w-full">
        {/* Specific Joints Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>Are there specific joints affected? *</strong>
          </label>
          <Radio.Group
            onChange={(e) =>
              handleUpdateValue("impactedJoints", e.target.value)
            }
            value={impactedJoints}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {/* Swelling Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Is there swelling or inflammation associated with the pain? *
            </strong>
          </label>
          <Radio.Group
            onChange={(e) => handleUpdateValue("swelling", e.target.value)}
            value={swelling}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {/* Sleep Interference Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>Do you have spasms that interfere with sleep? *</strong>
          </label>
          <Radio.Group
            onChange={(e) =>
              handleUpdateValue("sleepInterference", e.target.value)
            }
            value={sleepInterference}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {/* Burning Sensation Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>Is there a burning or tingling sensation? *</strong>
          </label>
          <Radio.Group
            onChange={(e) =>
              handleUpdateValue("burningSensation", e.target.value)
            }
            value={burningSensation}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {/* Specific Stimuli Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Does the pain respond to specific stimuli (e.g., light touch)? *
            </strong>
          </label>
          <Radio.Group
            onChange={(e) =>
              handleUpdateValue("specificStimuli", e.target.value)
            }
            value={specificStimuli}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>
        {/* Error Display */}
        {isError === true && (
          <div className="mt-6">
            <ErrorList errors={errors} />
          </div>
        )}

        {/* Submit Button */}
        <div
          className="flex justify-center gap-4"
          style={{ marginTop: "40px" }}
        >
          <button
            onClick={handleBack}
            className="caption2 transition-colors duration-200"
            style={{
              color: "var(--primary-500)",
              textDecoration: "none",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isLoading}
            style={{ position: "relative", minWidth: "120px" }}
          >
            {isLoading ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Spin size="small" />
                Saving...
              </span>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcConditionspecificq;
